import { CrossUI } from 'phx-core';

import {
  Component,
  EventEmitter,
  Output,
  OnInit,
  HostBinding,
  SecurityContext,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { FeatureUpgradeService } from '@misc/upsell/upgrade.service';
import { NzMessageService } from 'ng-zorro-antd';
import { FleetCardsFeatureFlagService } from '@services/feature-flags/fleet-cards';
import { Session } from '@services/session';

import { CTA_ACTION_TYPES, EXPANSION_UPSELL_TYPES, ExpansionUpsellService } from '../../service';
import { ExpansionUpsellConfig, IExpansionUpsellsData, IExpansionUpsellsStickyBannerUpsell, IUpsellCTA } from '../../interface';

@Component({
  selector: 'app-expansion-upsells-banner',
  templateUrl: './expansion-upsell-banner.component.html',
  styleUrls: ['./expansion-upsell-banner.component.scss'],
})
export class ExpansionUpsellsBannerComponent implements OnInit {
  @Output() public onBannerClose = new EventEmitter();
  @Output() public onBannerOpen = new EventEmitter();
  @HostBinding('class.hide') private shouldHideBanner: boolean = true;

  public readonly crossIcon: string = CrossUI;

  public content?: SafeHtml;
  public CTAs: IUpsellCTA[] = [];
  private upsellType: keyof typeof EXPANSION_UPSELL_TYPES = EXPANSION_UPSELL_TYPES.BANNER as keyof typeof EXPANSION_UPSELL_TYPES;
  private industry: string;

  constructor(
    private service: ExpansionUpsellService,
    private sanitzer: DomSanitizer,
    private featureUpgradeService: FeatureUpgradeService,
    private message: NzMessageService,
    private featureFlagService: FleetCardsFeatureFlagService,
    private session: Session,
  ) { }

  public ngOnInit() {
    if (this.session.isDriver() || !this.service.isFeatureFlagEnabled()) return;
    this.service.getIndustry().subscribe((segments: string[]) => {
      const config: IExpansionUpsellsData = this.service.getDynamicConfig();
      const upsellIndustries: Set<string> = new Set(config.upsells.map((conf: ExpansionUpsellConfig) => conf.industry));

      const matchingIndustries: string[] = segments.filter(segment => upsellIndustries.has(segment));

      if (matchingIndustries.length === 1) {
        this.industry = matchingIndustries[0];
        this.initUpsell();
      }
    });
  }

  private initUpsell(): void {
    if (this.service.shouldShowAGUpsell(this.upsellType, this.industry)) {
      const upsellConfig: IExpansionUpsellsStickyBannerUpsell = this.service.getUpsellConfig(
        this.upsellType,
        this.industry,
      ) as IExpansionUpsellsStickyBannerUpsell;
      if (upsellConfig) {
        this.content = this.sanitzer.sanitize(
          SecurityContext.HTML,
          upsellConfig.variants[this.getVariant()].heading[this.service.getUserLanguage()],
        );
        this.CTAs = upsellConfig.variants[this.getVariant()].cta;
        this.showBanner();
      }
    }
  }

  private getVariant(): number {
    return this.featureFlagService.getFleetCardsExpansionUpsellsStickyBannerVariant();
  }

  public getCTAText(cta: IUpsellCTA): string {
    return cta.text[this.service.getUserLanguage()];
  }

  public handleCTAClick(cta: IUpsellCTA): void {
    if (cta.action.type === CTA_ACTION_TYPES.generateLead) {
      this.featureUpgradeService.genericUpsellWithCustomProps(
        cta.action.campaign_name,
        cta.text[this.service.getUserLanguage()],
        cta.action.offer_type,
        `${cta.action.utm_content}-${this.getVariant() === 0 ? 'A' : 'B'}`,
        cta.action.product_interest,
      ).subscribe(() => {
        this.message.success($localize`Thank you for your interest. We will reach out to you shortly.`);
        this.completeUpsell(cta);
      });
    }
    if (cta.action.type === CTA_ACTION_TYPES.redirect) {
      this.featureUpgradeService.genericUpsellWithCustomProps(
        cta.action.campaign_name,
        cta.text[this.service.getUserLanguage()],
        cta.action.offer_type,
        `${cta.action.utm_content}-${this.getVariant() === 0 ? 'A' : 'B'}`,
        cta.action.product_interest,
      ).subscribe(() => {
        window.open(cta.action.route, '_blank');
        this.temporarilyDismissBanner(cta);
      });
    }
  }

  private temporarilyDismissBanner(cta: IUpsellCTA): void {
    this.onBannerClose.emit();
    this.shouldHideBanner = true;
    this.service.markUpsellAsTemporarilyDismissed(this.upsellType, this.industry);
    this.service.trackEvent(this.upsellType, this.industry, this.getVariant(), cta);
  }

  private completeUpsell(cta: IUpsellCTA): void {
    this.onBannerClose.emit();
    this.shouldHideBanner = true;
    this.service.markUpsellAsCompleted(this.upsellType, this.industry);
    this.service.trackEvent(this.upsellType, this.industry, this.getVariant(), cta);
  }

  public showBanner(): void {
    this.service.markUpsellAsShown(this.upsellType, this.industry);
    this.service.trackEvent(this.upsellType, this.industry, this.getVariant(), 'View');
    this.onBannerOpen.emit();
    this.shouldHideBanner = false;
  }

  public closeBanner(): void {
    this.onBannerClose.emit();
    this.shouldHideBanner = true;
    this.service.markUpsellAsDismissed(this.upsellType, this.industry);
    this.service.trackEvent(this.upsellType, this.industry, this.getVariant(), 'Dismiss');
  }
}